import React from 'react';
import { ArrowLeft, MapPin, Bed, Bath, Home, Calendar, Phone, User } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Vacancy } from '@/stores/useLandlordStore';

interface LocationState {
  vacancy: Partial<Vacancy> & {
    property_name?: string;
    unit_name?: string;
    bedrooms?: number;
    bathrooms?: number;
    amenities?: string[];
    isEdit?: boolean;
  };
}

export const VacancyPreview: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { vacancy } = (location.state as LocationState) || { vacancy: null };

  if (!vacancy) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center py-12">
          <p className="text-light-subtle dark:text-dark-subtle">No vacancy data to preview.</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 text-primary hover:underline"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const inputClass =
    "w-full px-4 py-3 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/50 transition-shadow";

  return (
    <div className="max-w-4xl mx-auto pb-12 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4 bg-light-card dark:bg-dark-card p-4 rounded-lg shadow-sm border border-light-border dark:border-dark-border">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
        >
          <ArrowLeft size={24} className="text-primary" />
        </button>
        <h1 className="text-xl font-bold text-light-text dark:text-dark-text">
          {vacancy.isEdit ? 'Preview Changes' : 'Preview Vacancy'}
        </h1>
      </div>

      {/* Image Gallery */}
      <div className="bg-light-card dark:bg-dark-card rounded-xl overflow-hidden shadow-sm border border-light-border dark:border-dark-border">
        {vacancy.images && vacancy.images.length > 0 ? (
          <div className="relative">
            <img
              src={vacancy.images[0]}
              alt={vacancy.title}
              className="w-full h-64 md:h-80 object-cover"
            />
            {vacancy.images.length > 1 && (
              <div className="absolute bottom-4 right-4 px-3 py-1 bg-black/60 rounded-full text-white text-sm">
                {vacancy.images.length} photos
              </div>
            )}
          </div>
        ) : (
          <div className="w-full h-64 md:h-80 bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
            <Home size={64} className="text-light-subtle dark:text-dark-subtle" />
          </div>
        )}
      </div>

      {/* Main Details */}
      <div className="bg-light-card dark:bg-dark-card p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border space-y-6">
        {/* Title and Price */}
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-light-text dark:text-dark-text">
              {vacancy.title}
            </h2>
            <div className="flex items-center gap-2 mt-2 text-light-subtle dark:text-dark-subtle">
              <MapPin size={16} />
              <span>{vacancy.property_name || 'Property Name'}</span>
              {vacancy.unit_name && (
                <>
                  <span>•</span>
                  <span>{vacancy.unit_name}</span>
                </>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-primary">
              KES {vacancy.rent_amount?.toLocaleString() || 0}
            </p>
            <p className="text-light-subtle dark:text-dark-subtle text-sm">/month</p>
          </div>
        </div>

        {/* Property Features */}
        <div className="flex gap-6 py-4 border-t border-b border-light-border dark:border-dark-border">
          {vacancy.bedrooms !== undefined && (
            <div className="flex items-center gap-2">
              <Bed size={20} className="text-primary" />
              <div>
                <p className="text-sm text-light-subtle dark:text-dark-subtle">Bedrooms</p>
                <p className="font-semibold text-light-text dark:text-dark-text">{vacancy.bedrooms}</p>
              </div>
            </div>
          )}
          {vacancy.bathrooms !== undefined && (
            <div className="flex items-center gap-2">
              <Bath size={20} className="text-primary" />
              <div>
                <p className="text-sm text-light-subtle dark:text-dark-subtle">Bathrooms</p>
                <p className="font-semibold text-light-text dark:text-dark-text">{vacancy.bathrooms}</p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Calendar size={20} className="text-primary" />
            <div>
              <p className="text-sm text-light-subtle dark:text-dark-subtle">Available</p>
              <p className="font-semibold text-light-text dark:text-dark-text">
                {vacancy.available_date
                  ? new Date(vacancy.available_date).toLocaleDateString()
                  : 'Now'}
              </p>
            </div>
          </div>
        </div>

        {/* Description */}
        <div>
          <h3 className="font-semibold text-light-text dark:text-dark-text mb-3">Description</h3>
          <p className="text-light-text dark:text-dark-text leading-relaxed">
            {vacancy.description || 'No description provided.'}
          </p>
        </div>

        {/* Amenities */}
        {vacancy.amenities && vacancy.amenities.length > 0 && (
          <div>
            <h3 className="font-semibold text-light-text dark:text-dark-text mb-3">Amenities</h3>
            <div className="flex flex-wrap gap-2">
              {vacancy.amenities.map((amenity, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                >
                  {amenity}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Additional Images */}
        {vacancy.images && vacancy.images.length > 1 && (
          <div>
            <h3 className="font-semibold text-light-text dark:text-dark-text mb-3">
              Additional Photos
            </h3>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
              {vacancy.images.slice(1).map((image, idx) => (
                <img
                  key={idx}
                  src={image}
                  alt={`Property ${idx + 2}`}
                  className="w-full h-24 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity"
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={() => navigate(-1)}
          className="flex-1 py-3 text-center bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-xl font-bold text-base hover:opacity-90 transition-opacity"
        >
          Edit Details
        </button>
        <button
          onClick={() => {
            // Handle publish/update
            navigate('/landlord/vacancies');
          }}
          className="flex-1 py-3 text-center bg-primary text-white rounded-xl font-bold text-base hover:opacity-90 transition-opacity"
        >
          {vacancy.isEdit ? 'Save Changes' : 'Publish Vacancy'}
        </button>
      </div>
    </div>
  );
};